import React, { FC, useEffect, useState } from "react";
import { Sidebar } from "@/app/layout/sidebar";
import styled from "@emotion/styled";
import { SIDEBAR_WIDTH, MOBILE_HEADER_HEIGHT, SIDEBAR_PADDING } from "@/app/layout/constants";
import { MobileHeader } from "@/app/layout/mobile-header";

interface SidebarLayoutProps {
  children: React.ReactNode;
}

export const SidebarLayout: FC<SidebarLayoutProps> = ({ children }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isMobile && sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobile, sidebarOpen]);

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <LayoutContainer>
      {isMobile && (
        <>
          <MobileHeader onMenuClick={() => setSidebarOpen(true)} />
          <SidebarOverlay open={sidebarOpen} onClick={closeSidebar} />
        </>
      )}
      <SidebarWrapper isMobile={isMobile} open={isMobile ? sidebarOpen : true}>
        <Sidebar onNavigate={closeSidebar} />
      </SidebarWrapper>
      <MainContent $isMobile={isMobile}>
        <PaperContainer $isMobile={isMobile}>{children}</PaperContainer>
      </MainContent>
    </LayoutContainer>
  );
};

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
`;

const MainContent = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: #eeeeee;
  padding: 0;
  min-height: 100vh;
  padding-left: ${({ $isMobile }) => ($isMobile ? 0 : `${SIDEBAR_WIDTH - SIDEBAR_PADDING}px`)};
  padding-top: ${({ $isMobile }) => ($isMobile ? `${MOBILE_HEADER_HEIGHT}px` : 0)};
  align-items: center;
  justify-content: flex-start;
`;

const PaperContainer = styled.div<{ $isMobile: boolean }>`
  background-color: rgb(255, 255, 255);
  min-height: calc(100vh - 56px);
  width: ${({ $isMobile }) => ($isMobile ? "calc(100% - 16px)" : "calc(100% - 64px)")};
  max-width: ${({ $isMobile }) => ($isMobile ? "100%" : "960px")};
  min-width: ${({ $isMobile }) => ($isMobile ? "0" : "600px")};
  margin: ${({ $isMobile }) => ($isMobile ? "8px" : "28px")};
  padding: 14px 14px 3.5px 14px;
  border: 1px solid rgb(136, 136, 136);
  box-shadow: rgb(204, 204, 204) 2px 2px 0px 0px;
  box-sizing: border-box;
  overflow-x: hidden;
  line-height: 21px;
  text-rendering: optimizelegibility;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
`;

const SidebarWrapper = styled.div<{ isMobile: boolean; open: boolean }>`
  position: fixed;
  top: ${({ isMobile }) => (isMobile ? `${MOBILE_HEADER_HEIGHT}px` : 0)};
  left: 0;
  bottom: 0;
  width: ${SIDEBAR_WIDTH}px;
  min-width: ${SIDEBAR_WIDTH}px;
  transform: translateX(
    ${({ isMobile, open }) => (isMobile ? (open ? "0" : `-${SIDEBAR_WIDTH}px`) : "0")}
  );
  transition: none;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 3;
  -webkit-overflow-scrolling: touch;
  contain: layout style paint;
`;

const SidebarOverlay = styled.div<{ open: boolean }>`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.4);
  opacity: ${({ open }) => (open ? 1 : 0)};
  pointer-events: ${({ open }) => (open ? "auto" : "none")};
  transition: none;
  z-index: 2;
`;
